import type { DTO, Nullable } from '@/type-utils';
import { removeUndefined } from '@/utils/object-utils';

import type { IPrice } from '.';
import { MoneyModel } from '../Money';

import Model from '../Model';

/**
 * Represents a product's price with support for sale pricing. Provides
 * on-demand discount calculations.
 */
export default class PriceModel extends Model<DTO<IPrice>> implements IPrice {
  /** @inheritDoc */
  public readonly price: MoneyModel;

  /** @inheritDoc */
  public readonly originalPrice?: Nullable<MoneyModel>;

  /** @inheritdoc */
  public constructor(dto: DTO<IPrice>) {
    super(dto);

    this.price = MoneyModel.from(dto.price);

    if (dto.originalPrice) {
      this.originalPrice = MoneyModel.from(dto.originalPrice);
    }
  }

  /**
   * Calculates the discount percentage on demand.
   * Returns `null` if no discount is available.
   *
   * @returns The discount percentage (0-100) or `null` if not on sale.
   */
  public get discountPercent(): Nullable<number> {
    if (!this.originalPrice) return null;
    if (!this.originalPrice.isGreaterThan(this.price)) return null;

    // Calculate discount percentage using MoneyModel's precise arithmetic
    const discount = MoneyModel.subtract(this.originalPrice, this.price);
    return Math.round(discount.percentageOf(this.originalPrice));
  }

  /**
   * Determines if the product is currently on sale.
   *
   * @returns `true` if originalPrice exists and is greater than price.
   */
  public get isOnSale(): boolean {
    return Boolean(this.originalPrice?.isGreaterThan(this.price));
  }

  // Backwards compatibility properties (deprecated)

  /**
   * @deprecated Use `price` or `originalPrice` instead.
   * @returns The retail price for backwards compatibility.
   */
  public get retailPrice(): MoneyModel {
    // If originalPrice exists, that's the retail price
    // Otherwise, price is the retail price
    return this.originalPrice ?? this.price;
  }

  /**
   * @deprecated Use `price` instead when on sale.
   * @returns The current sale price for backwards compatibility.
   */
  public get currentPrice(): Nullable<MoneyModel> {
    return this.price;
  }

  /**
   * @deprecated Use `isOnSale` instead.
   * @returns Whether the product has a discount for backwards compatibility.
   */
  public get isDiscounted(): boolean {
    return this.isOnSale;
  }

  /** @inheritdoc */
  public toDTO(): DTO<IPrice> {
    return removeUndefined({
      price: this.price.toDTO(),
      originalPrice: this.originalPrice?.toDTO()
    }) as DTO<IPrice>;
  }
}
